import { useState, useEffect, useCallback } from 'react';
import { setTranscriptionPatterns, type TranscriptionPatternsConfig, type TranscriptionPattern } from '../lib/cefr-validator';
import { fetchGrammarPatterns, fetchGrammarCoverage, improveGrammar, qaGrammar, researchGrammar, dedupGrammar, type GrammarPattern, type CoverageMap, type GrammarActionResult } from '../lib/grammar-client';

const PATTERN_COLORS: Record<string, { bg: string; text: string; badge: string }> = {
  A: { bg: 'bg-zinc-900/30', text: 'text-zinc-300', badge: 'bg-zinc-700 text-zinc-300' },
  B: { bg: 'bg-blue-900/30', text: 'text-blue-300', badge: 'bg-blue-900 text-blue-300' },
  C: { bg: 'bg-teal-900/30', text: 'text-teal-300', badge: 'bg-teal-900 text-teal-300' },
  D: { bg: 'bg-amber-900/30', text: 'text-amber-300', badge: 'bg-amber-900 text-amber-300' },
  E: { bg: 'bg-indigo-900/30', text: 'text-indigo-300', badge: 'bg-indigo-900 text-indigo-300' },
  F: { bg: 'bg-rose-900/30', text: 'text-rose-300', badge: 'bg-rose-900 text-rose-300' },
};

const DEFAULT_COLORS = { bg: 'bg-zinc-900/30', text: 'text-zinc-300', badge: 'bg-zinc-700 text-zinc-300' };

const GRAMMAR_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2', 'SPEC'] as const;
const GRAMMAR_CATEGORIES = ['verb', 'noun', 'adjective', 'adverb', 'particle', 'conjunction', 'language_specific'] as const;

type Tab = 'transcription' | 'grammar' | 'coverage' | 'langdata' | 'datasources';

function PatternBadge({ code }: { code: string }) {
  const c = PATTERN_COLORS[code] ?? DEFAULT_COLORS;
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-bold ${c.badge}`}>
      {code}
    </span>
  );
}

function LevelPills({ levels }: { levels: string[] }) {
  if (levels.length === 0) return <span className="text-zinc-500 text-xs">none</span>;
  return (
    <span className="flex gap-1 flex-wrap">
      {levels.map(l => (
        <span key={l} className="px-1.5 py-0.5 rounded text-xs bg-zinc-700 text-zinc-300">{l}</span>
      ))}
    </span>
  );
}

function LangTag({ code, onRemove }: { code: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-zinc-700 text-zinc-200 text-xs font-mono group">
      {code}
      <button
        onClick={onRemove}
        className="text-zinc-500 hover:text-red-400 transition-colors ml-0.5"
        title={`Remove ${code}`}
      >
        x
      </button>
    </span>
  );
}

interface PatternCardProps {
  code: string;
  pattern: TranscriptionPattern;
  onRemoveLang: (lang: string) => void;
  onAddLang: (lang: string) => void;
  allAssigned: Set<string>;
}

function PatternCard({ code, pattern, onRemoveLang, onAddLang, allAssigned }: PatternCardProps) {
  const [addInput, setAddInput] = useState('');
  const [addError, setAddError] = useState('');
  const c = PATTERN_COLORS[code] ?? DEFAULT_COLORS;
  const sorted = [...pattern.languages].sort();

  const handleAdd = () => {
    const val = addInput.trim().toUpperCase();
    if (!val) return;
    if (pattern.languages.indexOf(val) !== -1) {
      setAddError('Already in this pattern');
      return;
    }
    if (allAssigned.has(val)) {
      setAddError(`Already assigned to another pattern`);
      return;
    }
    if (!/^[A-Z][A-Z0-9_]{1,9}$/.test(val)) {
      setAddError('Invalid code format');
      return;
    }
    onAddLang(val);
    setAddInput('');
    setAddError('');
  };

  return (
    <div className={`rounded-lg border border-white/5 ${c.bg} p-4`}>
      <div className="flex items-center gap-3 mb-3">
        <PatternBadge code={code} />
        <h3 className={`font-heading text-lg font-semibold ${c.text}`}>{pattern.title}</h3>
      </div>

      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm mb-3">
        <div className="text-zinc-400">Transcription</div>
        <div className="text-zinc-200 font-mono">{pattern.transcription}</div>
        <div className="text-zinc-400">Transcription levels</div>
        <div><LevelPills levels={pattern.levels} /></div>
        <div className="text-zinc-400">[GLO] levels</div>
        <div><LevelPills levels={pattern.glo_levels} /></div>
        {pattern.vocalization && Object.keys(pattern.vocalization).length > 0 && (<>
          <div className="text-zinc-400">Vocalization</div>
          <div className="text-zinc-200 text-xs">
            {Object.entries(pattern.vocalization).map(([lang, type]) => (
              <span key={lang} className="inline-block mr-1.5 mb-0.5 px-1.5 py-0.5 rounded bg-amber-900/40 text-amber-300 font-mono">{lang} ({type})</span>
            ))}
          </div>
          <div className="text-zinc-400">Vocalization levels</div>
          <div><LevelPills levels={pattern.vocalization_levels || []} /></div>
        </>)}
      </div>

      <p className="text-xs text-zinc-500 mb-3 italic">{pattern.note}</p>

      <div className="border-t border-white/5 pt-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-zinc-400 font-medium">Languages ({sorted.length})</span>
        </div>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {sorted.map(lang => (
            <LangTag key={lang} code={lang} onRemove={() => onRemoveLang(lang)} />
          ))}
        </div>
        <div className="flex gap-2 items-center">
          <input
            type="text"
            value={addInput}
            onChange={e => { setAddInput(e.target.value); setAddError(''); }}
            onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }}
            placeholder="Add code..."
            className="w-32 px-2 py-1 rounded bg-zinc-800 border border-zinc-600 text-zinc-200 text-xs font-mono placeholder:text-zinc-600 focus:outline-none focus:border-teal-500"
          />
          <button
            onClick={handleAdd}
            className="px-3 py-1 rounded bg-teal-900/50 text-teal-300 text-xs font-medium hover:bg-teal-900/80 transition-colors"
          >
            Add
          </button>
          {addError && <span className="text-xs text-red-400">{addError}</span>}
        </div>
      </div>
    </div>
  );
}

// --- Grammar Patterns Tab ---

function GrammarTab() {
  const [coverage, setCoverage] = useState<CoverageMap | null>(null);
  const [patterns, setPatterns] = useState<GrammarPattern[]>([]);
  const [selectedLang, setSelectedLang] = useState('');
  const [loadingCoverage, setLoadingCoverage] = useState(true);
  const [loadingPatterns, setLoadingPatterns] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [levelFilter, setLevelFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [search, setSearch] = useState('');
  const [sortCol, setSortCol] = useState<'pattern_id' | 'level' | 'category'>('pattern_id');
  const [sortAsc, setSortAsc] = useState(true);
  const [customLang, setCustomLang] = useState('');
  const [actionRunning, setActionRunning] = useState<'improve' | 'qa' | 'research' | 'dedup' | null>(null);
  const [actionLog, setActionLog] = useState<Array<{ action: string; lang: string; result: GrammarActionResult; time: string }>>([]);

  useEffect(() => {
    fetchGrammarCoverage()
      .then(cov => {
        setCoverage(cov);
        const langs = Object.keys(cov).sort();
        if (langs.length > 0 && !selectedLang) setSelectedLang(langs[0]!);
      })
      .catch(err => setError(err instanceof Error ? err.message : 'Failed to load coverage'))
      .finally(() => setLoadingCoverage(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedLang) return;
    setLoadingPatterns(true);
    setError(null);
    fetchGrammarPatterns(selectedLang)
      .then(items => setPatterns(items))
      .catch(err => setError(err instanceof Error ? err.message : 'Failed to load patterns'))
      .finally(() => setLoadingPatterns(false));
  }, [selectedLang]);

  if (loadingCoverage) {
    return <div className="text-zinc-400 text-sm py-8 text-center">Loading grammar coverage...</div>;
  }

  if (!coverage) {
    return <div className="text-red-400 text-sm py-8 text-center">{error ?? 'No coverage data'}</div>;
  }

  const langs = Object.keys(coverage).sort();

  const filtered = patterns.filter(p => {
    if (levelFilter && p.level !== levelFilter) return false;
    if (categoryFilter && p.category !== categoryFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return p.pattern.toLowerCase().includes(q) ||
        p.romanization.toLowerCase().includes(q) ||
        p.function.toLowerCase().includes(q) ||
        p.pattern_id.includes(q);
    }
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    let cmp = 0;
    if (sortCol === 'pattern_id') cmp = Number(a.pattern_id) - Number(b.pattern_id);
    else if (sortCol === 'level') cmp = a.level.localeCompare(b.level);
    else if (sortCol === 'category') cmp = a.category.localeCompare(b.category);
    return sortAsc ? cmp : -cmp;
  });

  const handleSort = (col: typeof sortCol) => {
    if (sortCol === col) setSortAsc(!sortAsc);
    else { setSortCol(col); setSortAsc(true); }
  };

  const runAction = async (action: 'improve' | 'qa' | 'research' | 'dedup') => {
    if (!selectedLang || actionRunning) return;
    setActionRunning(action);
    try {
      const fn = action === 'improve' ? improveGrammar : action === 'qa' ? qaGrammar : action === 'dedup' ? dedupGrammar : researchGrammar;
      const result = await fn(selectedLang);
      setActionLog(prev => [{ action, lang: selectedLang, result, time: new Date().toLocaleTimeString() }, ...prev]);
      if (result.ok) {
        const items = await fetchGrammarPatterns(selectedLang);
        setPatterns(items);
        const cov = await fetchGrammarCoverage();
        setCoverage(cov);
      }
    } catch (err) {
      setActionLog(prev => [{
        action, lang: selectedLang, time: new Date().toLocaleTimeString(),
        result: { ok: false, error: err instanceof Error ? err.message : 'Request failed' },
      }, ...prev]);
    } finally {
      setActionRunning(null);
    }
  };

  const levelCounts: Record<string, number> = {};
  const catCounts: Record<string, number> = {};
  for (const p of patterns) {
    levelCounts[p.level] = (levelCounts[p.level] ?? 0) + 1;
    catCounts[p.category] = (catCounts[p.category] ?? 0) + 1;
  }

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <label className="block text-xs text-zinc-500 mb-1">Language</label>
          <div className="flex items-center gap-1">
            <select value={langs.includes(selectedLang) ? selectedLang : ''} onChange={e => { setSelectedLang(e.target.value); setCustomLang(''); }}
              className="px-3 py-1.5 rounded-l bg-zinc-800 border border-zinc-600 text-zinc-200 text-sm font-mono focus:outline-none focus:border-teal-500">
              {!langs.includes(selectedLang) && <option value="">—</option>}
              {langs.map(l => (
                <option key={l} value={l}>{l} ({coverage[l]})</option>
              ))}
            </select>
            <input type="text" value={customLang} onChange={e => setCustomLang(e.target.value.toUpperCase())}
              onKeyDown={e => { if (e.key === 'Enter' && customLang.trim()) { setSelectedLang(customLang.trim()); } }}
              placeholder="or type code"
              className="w-28 px-2 py-1.5 rounded-r bg-zinc-800 border border-zinc-600 border-l-0 text-zinc-200 text-sm font-mono placeholder:text-zinc-600 focus:outline-none focus:border-teal-500" />
            {customLang.trim() && customLang.trim() !== selectedLang && (
              <button onClick={() => { setSelectedLang(customLang.trim()); }}
                className="px-2 py-1.5 rounded bg-teal-900/50 text-teal-300 text-xs hover:bg-teal-900/80">Go</button>
            )}
          </div>
        </div>
        <div>
          <label className="block text-xs text-zinc-500 mb-1">Level</label>
          <select value={levelFilter} onChange={e => setLevelFilter(e.target.value)}
            className="px-3 py-1.5 rounded bg-zinc-800 border border-zinc-600 text-zinc-200 text-sm focus:outline-none focus:border-teal-500">
            <option value="">All levels</option>
            {GRAMMAR_LEVELS.map(l => (
              <option key={l} value={l}>{l} ({levelCounts[l] ?? 0})</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-zinc-500 mb-1">Category</label>
          <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
            className="px-3 py-1.5 rounded bg-zinc-800 border border-zinc-600 text-zinc-200 text-sm focus:outline-none focus:border-teal-500">
            <option value="">All categories</option>
            {GRAMMAR_CATEGORIES.map(c => (
              <option key={c} value={c}>{c} ({catCounts[c] ?? 0})</option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs text-zinc-500 mb-1">Search</label>
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search pattern, romanization, function..."
            className="w-full px-3 py-1.5 rounded bg-zinc-800 border border-zinc-600 text-zinc-200 text-sm placeholder:text-zinc-600 focus:outline-none focus:border-teal-500" />
        </div>
      </div>

      {/* Stats bar + action buttons */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 text-xs text-zinc-500">
          <span>{patterns.length} total patterns</span>
          <span>{filtered.length} shown</span>
          {loadingPatterns && <span className="text-teal-400">Loading...</span>}
          {error && <span className="text-red-400">{error}</span>}
        </div>
        {selectedLang && (
          <div className="flex items-center gap-2">
            <button onClick={() => runAction('improve')} disabled={!!actionRunning}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${actionRunning === 'improve' ? 'bg-teal-800 text-teal-200 animate-pulse' : actionRunning ? 'bg-zinc-700 text-zinc-500 cursor-not-allowed' : 'bg-teal-900/50 text-teal-300 hover:bg-teal-900/80'}`}>
              {actionRunning === 'improve' ? 'Improving...' : 'Improve'}
            </button>
            <button onClick={() => runAction('qa')} disabled={!!actionRunning}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${actionRunning === 'qa' ? 'bg-cyan-800 text-cyan-200 animate-pulse' : actionRunning ? 'bg-zinc-700 text-zinc-500 cursor-not-allowed' : 'bg-cyan-900/50 text-cyan-300 hover:bg-cyan-900/80'}`}>
              {actionRunning === 'qa' ? 'Running QA...' : 'QA'}
            </button>
            <button onClick={() => runAction('research')} disabled={!!actionRunning}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${actionRunning === 'research' ? 'bg-violet-800 text-violet-200 animate-pulse' : actionRunning ? 'bg-zinc-700 text-zinc-500 cursor-not-allowed' : 'bg-violet-900/50 text-violet-300 hover:bg-violet-900/80'}`}>
              {actionRunning === 'research' ? 'Researching...' : 'Research'}
            </button>
            <button onClick={() => runAction('dedup')} disabled={!!actionRunning}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${actionRunning === 'dedup' ? 'bg-amber-800 text-amber-200 animate-pulse' : actionRunning ? 'bg-zinc-700 text-zinc-500 cursor-not-allowed' : 'bg-amber-900/50 text-amber-300 hover:bg-amber-900/80'}`}>
              {actionRunning === 'dedup' ? 'Deduping...' : 'Dedup'}
            </button>
          </div>
        )}
      </div>

      {/* Action log */}
      {actionLog.length > 0 && (
        <div className="border border-zinc-700 rounded-lg p-3 space-y-2 max-h-[60vh] overflow-y-auto">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-zinc-400">Action Log</span>
            <button onClick={() => setActionLog([])} className="text-xs text-zinc-600 hover:text-zinc-400">Clear</button>
          </div>
          {actionLog.map((entry, i) => (
            <div key={i} className={`text-xs rounded px-2 py-1.5 ${entry.result.ok ? 'bg-teal-900/20 text-teal-300' : 'bg-red-900/20 text-red-300'}`}>
              <span className="text-zinc-500">{entry.time}</span>
              {' '}<span className="font-medium">{entry.action.toUpperCase()}</span>
              {' '}<span className="font-mono">{entry.lang}</span>
              {' — '}
              {entry.result.ok ? (
                <span>
                  {entry.result.added != null && `+${entry.result.added} added `}
                  {entry.result.filled != null && `(${entry.result.filled} filled, ${entry.result.novel ?? 0} novel) `}
                  {entry.result.remapped != null && entry.result.remapped > 0 && `${entry.result.remapped} remapped `}
                  {entry.result.unfilled_remaining != null && `${entry.result.unfilled_remaining} gaps remain `}
                  {entry.result.reviewed != null && `${entry.result.reviewed} reviewed `}
                  {entry.result.fixes != null && `${entry.result.fixes} fixes `}
                  {entry.result.stored && `Research stored (${Math.round((entry.result.research_length ?? 0) / 1000)}k chars) `}
                  {entry.result.message ?? ''}
                </span>
              ) : (
                <span>{entry.result.error ?? 'Failed'}</span>
              )}
              {entry.result.steps && entry.result.steps.length > 0 && (
                <details className="mt-1" open={!entry.result.ok}>
                  <summary className="cursor-pointer text-zinc-500 hover:text-zinc-300">Lambda steps ({entry.result.steps.length})</summary>
                  <div className="mt-1 rounded bg-zinc-950 border border-zinc-800 p-2 font-mono text-[11px] leading-relaxed max-h-48 overflow-y-auto">
                    {entry.result.steps.map((step, j) => (
                      <div key={j} className="text-zinc-400">
                        <span className="text-zinc-600 select-none">{String(j + 1).padStart(2, ' ')}.</span> {step}
                      </div>
                    ))}
                  </div>
                </details>
              )}
              {entry.result.qa_log && entry.result.qa_log.length > 0 && (
                <details className="mt-1">
                  <summary className="cursor-pointer text-zinc-500 hover:text-zinc-300">{entry.result.qa_log.length} detail(s)</summary>
                  <div className="mt-1 space-y-1 pl-2 border-l border-zinc-700">
                    {entry.result.qa_log.map((q, j) => (
                      <div key={j} className="text-xs text-zinc-400">
                        <span className="font-mono">{q.pattern_id}</span> {q.field}: <span className="text-zinc-500">{q.original}</span> → <span className="text-zinc-200">{q.fixed}</span> <span className="text-zinc-600">({q.note})</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
              {entry.result.preview && (
                <details className="mt-1">
                  <summary className="cursor-pointer text-zinc-500 hover:text-zinc-300">Research preview</summary>
                  <pre className="mt-1 text-xs text-zinc-400 whitespace-pre-wrap max-h-64 overflow-y-auto pl-2 border-l border-violet-800">{entry.result.preview}</pre>
                </details>
              )}
              {!entry.result.ok && entry.result.raw && (
                <details className="mt-1">
                  <summary className="cursor-pointer text-zinc-500 hover:text-zinc-300">Raw response</summary>
                  <pre className="mt-1 rounded bg-zinc-950 border border-zinc-800 p-2 text-[11px] text-zinc-500 whitespace-pre-wrap max-h-48 overflow-y-auto font-mono">{entry.result.raw}</pre>
                </details>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Results table */}
      <div className="overflow-x-auto border border-zinc-700 rounded-lg">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-zinc-800">
              <th onClick={() => handleSort('pattern_id')}
                className="px-3 py-2 text-left text-xs font-medium text-zinc-400 cursor-pointer hover:text-zinc-200 w-16">
                ID {sortCol === 'pattern_id' ? (sortAsc ? '↑' : '↓') : ''}
              </th>
              <th onClick={() => handleSort('level')}
                className="px-3 py-2 text-left text-xs font-medium text-zinc-400 cursor-pointer hover:text-zinc-200 w-16">
                Level {sortCol === 'level' ? (sortAsc ? '↑' : '↓') : ''}
              </th>
              <th onClick={() => handleSort('category')}
                className="px-3 py-2 text-left text-xs font-medium text-zinc-400 cursor-pointer hover:text-zinc-200 w-28">
                Category {sortCol === 'category' ? (sortAsc ? '↑' : '↓') : ''}
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-zinc-400">Pattern</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-zinc-400">Romanization</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-zinc-400">Function</th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-zinc-500">
                {loadingPatterns ? 'Loading patterns...' : 'No patterns match the current filters'}
              </td></tr>
            )}
            {sorted.map((p, i) => (
              <tr key={`${p.pattern_id}-${i}`} className="border-t border-zinc-800/50 hover:bg-zinc-800/30">
                <td className="px-3 py-1.5 font-mono text-xs text-zinc-500">{p.pattern_id}</td>
                <td className="px-3 py-1.5">
                  <span className="px-1.5 py-0.5 rounded text-xs bg-zinc-700 text-zinc-300">{p.level}</span>
                </td>
                <td className="px-3 py-1.5 text-xs text-zinc-400">{p.category}</td>
                <td className="px-3 py-1.5 text-zinc-200">{p.pattern}</td>
                <td className="px-3 py-1.5 text-zinc-400 text-xs">{p.romanization}</td>
                <td className="px-3 py-1.5 text-zinc-400 text-xs">{p.function}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// --- Coverage Tab ---

function CoverageTab() {
  const [coverage, setCoverage] = useState<CoverageMap | null>(null);
  const [allPatterns, setAllPatterns] = useState<Map<string, GrammarPattern[]>>(new Map());
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchGrammarCoverage()
      .then(setCoverage)
      .catch(err => setError(err instanceof Error ? err.message : 'Failed'))
      .finally(() => setLoading(false));
  }, []);

  const loadLangDetail = useCallback(async (lang: string) => {
    if (allPatterns.has(lang)) return;
    setLoadingDetail(lang);
    try {
      const items = await fetchGrammarPatterns(lang);
      setAllPatterns(prev => {
        const next = new Map(prev);
        next.set(lang, items);
        return next;
      });
    } catch {
      // ignore
    } finally {
      setLoadingDetail(null);
    }
  }, [allPatterns]);

  if (loading) {
    return <div className="text-zinc-400 text-sm py-8 text-center">Loading coverage...</div>;
  }

  if (!coverage) {
    return <div className="text-red-400 text-sm py-8 text-center">{error ?? 'No data'}</div>;
  }

  const langs = Object.keys(coverage).sort();
  const levels = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2', 'SPEC'];
  const categories = ['verb', 'noun', 'adjective', 'adverb', 'particle', 'conjunction', 'language_specific'];

  const getLevelCount = (lang: string, level: string): number | null => {
    const items = allPatterns.get(lang);
    if (!items) return null;
    return items.filter(p => p.level === level).length;
  };

  const getCategoryCounts = (lang: string): Record<string, number> | null => {
    const items = allPatterns.get(lang);
    if (!items) return null;
    const counts: Record<string, number> = {};
    for (const p of items) {
      counts[p.category] = (counts[p.category] ?? 0) + 1;
    }
    return counts;
  };

  const maxCount = Math.max(...Object.values(coverage));

  return (
    <div className="space-y-6">
      <p className="text-sm text-zinc-400">
        {langs.length} languages with grammar patterns. Click a language to load its level/category breakdown.
      </p>

      {/* Main coverage grid: languages x levels */}
      <div className="overflow-x-auto border border-zinc-700 rounded-lg">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-zinc-800">
              <th className="px-3 py-2 text-left text-xs font-medium text-zinc-400 sticky left-0 bg-zinc-800 z-10 w-24">Language</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-zinc-400 min-w-[200px]">Total</th>
              {levels.map(l => (
                <th key={l} className="px-3 py-2 text-center text-xs font-medium text-zinc-400 w-20">{l}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {langs.map(lang => {
              const total = coverage[lang] ?? 0;
              const loaded = allPatterns.has(lang);
              const isLoading = loadingDetail === lang;
              const barWidth = maxCount > 0 ? Math.round((total / maxCount) * 100) : 0;
              return (
                <tr key={lang} className="border-t border-zinc-800/50 hover:bg-zinc-800/30 cursor-pointer"
                  onClick={() => loadLangDetail(lang)}>
                  <td className="px-3 py-1.5 font-mono text-xs text-teal-300 sticky left-0 bg-zinc-900 z-10">
                    {lang}
                    {isLoading && <span className="ml-2 text-zinc-500">loading...</span>}
                  </td>
                  <td className="px-3 py-1.5 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <div className="flex-1 h-2.5 bg-zinc-800 rounded overflow-hidden">
                        <div className="h-full bg-teal-600 rounded" style={{ width: `${barWidth}%` }} />
                      </div>
                      <span className="font-mono text-xs text-zinc-300 w-10 text-right">{total}</span>
                    </div>
                  </td>
                  {levels.map(level => {
                    const count = getLevelCount(lang, level);
                    return (
                      <td key={level} className="px-3 py-1.5 text-center">
                        {count !== null ? (
                          <span className={`font-mono text-xs ${count === 0 ? 'text-red-400' : 'text-zinc-300'}`}>
                            {count}
                          </span>
                        ) : (
                          <span className="text-zinc-700 text-xs">{loaded ? '0' : '-'}</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Category breakdown for loaded languages */}
      {langs.filter(l => allPatterns.has(l)).length > 0 && (
        <div>
          <h3 className="font-heading text-lg font-semibold text-zinc-200 mb-3">Category Breakdown</h3>
          <div className="overflow-x-auto border border-zinc-700 rounded-lg">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-zinc-800">
                  <th className="px-3 py-2 text-left text-xs font-medium text-zinc-400 sticky left-0 bg-zinc-800 z-10">Language</th>
                  {categories.map(c => (
                    <th key={c} className="px-3 py-2 text-center text-xs font-medium text-zinc-400 w-20">{c.replace('_', ' ')}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {langs.filter(l => allPatterns.has(l)).map(lang => {
                  const counts = getCategoryCounts(lang);
                  if (!counts) return null;
                  return (
                    <tr key={lang} className="border-t border-zinc-800/50 hover:bg-zinc-800/30">
                      <td className="px-3 py-1.5 font-mono text-xs text-teal-300 sticky left-0 bg-zinc-900 z-10">{lang}</td>
                      {categories.map(cat => {
                        const n = counts[cat] ?? 0;
                        return (
                          <td key={cat} className="px-3 py-1.5 text-center">
                            <span className={`font-mono text-xs ${n === 0 ? 'text-red-400' : 'text-zinc-300'}`}>{n}</span>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? 'https://ojbr4cjpvh.execute-api.ap-northeast-1.amazonaws.com';

// --- Claude Tier Lookup ---

const CLAUDE_TIER_1 = new Set([
  "eng","cmn","spa","fra","deu","jpn","por","ita","kor","rus","nld","arb","pol","tur","vie","swe","ces","ron","ell","dan",
  "nob","fin","hun","tha","hin","ukr","heb","ind","pes","cat","bul","hrv","srp","slk","zsm","ben","tam","urd","arz","tgl",
  "slv","ekk","lav","lit","afr","yue","swh","tel","mar","kat","eus","isl","glg","nno","guj","kan","mal","pan","cym","sqi",
  "azj","mkd","bos","hye","nep",
]);
const CLAUDE_TIER_2 = new Set([
  "gle","mya","khm","sin","khk","kaz","uzn","amh","hau","yor","ibo","zul","xho","lao","pbt","gla","mlt","mlg","kir","tgk",
  "tuk","som","kmr","ckb","ori","asm","snd","ceb","jav","sun","ltz","bel","tir","tat","bre","oci","lat","epo","san","bod",
  "mri","cos","fry","smo","ton","hat","haw","sot","tsn","sna","kin","aka","lin","nya","lug","nso","wol","ful","tso","ven",
  "nbl","ssw","nan","hak","fij","run","orm","bam","ewe","fon","grc","lzh","mai","bho","kok","doi","kas","mni","grn","quh",
  "aym","tpi","bis","cha","tog","bak","chv","uig","div","roh","arg","ast","srd","fao","glv","cor","hsb","dsb","sme","iku",
  "kal","chr","nav","yid","lad","dzo","tet","tok","ina","ido","vol","kik","luo","bem","ddo","kbd","oss","abk",
]);
const CLAUDE_TIER_3 = new Set([
  "ami","tay","pwn","bnn","tsu","trv","dru","pyu","xsy","ckv","ssf","szy","sxr","xnb","hil","bcl","war","pag","pam","ilo",
  "tsg","mdh","mrw","ifk","ace","bbc","min","bjn","bug","mak","ban","sas","nhn","zai","mix","maa","ote","yua","quc","tzo",
  "tzh","ady","ava","che","inh","lez","myv","mdf","mhr","udm","kpv","sah","tyv","bua","xal","sag","crk","ojg","tah","tig",
  "kab","tzm","shi","crh","gag","kaa","smn","sms","smj","sma","kon","lub","cjk","tum","mah","chk","pau","din","nus","knc",
  "dje","mos","dag","gaa","igl","bin","efi","ibb","tiv","nup","kjb","mam","cak","poi","cso","rmy","bal","mww","hnj","ksw",
  "shn","zha","iii","mhx","nxq","bca",
]);

function getClaudeTier(code: string): { tier: number; label: string; color: string } {
  if (CLAUDE_TIER_1.has(code)) return { tier: 1, label: "Tier 1 — Full Generation", color: "text-teal-400" };
  if (CLAUDE_TIER_2.has(code)) return { tier: 2, label: "Tier 2 — Reliable with Errors", color: "text-amber-400" };
  if (CLAUDE_TIER_3.has(code)) return { tier: 3, label: "Tier 3 — Partial, Needs Augmentation", color: "text-orange-400" };
  return { tier: 4, label: "Tier 4 — Minimal/Recognition Only", color: "text-red-400" };
}

function getGlossikaTier(claudeTier: number): { label: string; ops: string } {
  if (claudeTier === 1) return { label: "Glossika T1/T2", ops: "Full CEFR A0-C2 generation" };
  if (claudeTier === 2) return { label: "Glossika T3", ops: "A0-A2 with opus-4-7 only; B1+ blocked" };
  if (claudeTier === 3) return { label: "Glossika T3 restricted", ops: "Data-driven only; no sentence generation" };
  return { label: "Glossika T4", ops: "Display verified external data only" };
}

// --- Language Data Tab ---

interface LangProfile {
  language_code: string;
  name: string;
  family?: string;
  classification?: string;
  macroarea?: string;
  glottocode?: string;
  endangerment?: string;
  countries?: string;
  coordinates?: { lat: number; lng: number };
  phoneme_inventory?: {
    total: number;
    consonants: number;
    vowels: number;
    tones: number;
    consonant_list: string[];
    vowel_list: string[];
    tone_list: string[];
    inventories: string[];
  };
  typology?: Record<string, { name: string; value: string; label: string }>;
  wals_count?: number;
  grambank_count?: number;
  lexicon_entries?: number;
}

interface GapRow {
  code: string;
  name: string;
  claudeTier: number;
  grambank: number;
  wals: number;
  phonemes: number;
  lexicon: number;
  score: number;
}

const TIER_1_CODES = [...CLAUDE_TIER_1];

function LangDataTab() {
  const [langCode, setLangCode] = useState('');
  const [profile, setProfile] = useState<LangProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['meta', 'claude']));
  const [gapData, setGapData] = useState<GapRow[] | null>(null);
  const [gapLoading, setGapLoading] = useState(false);
  const [gapProgress, setGapProgress] = useState(0);
  const [gapSort, setGapSort] = useState<'score' | 'grambank' | 'wals' | 'phonemes' | 'lexicon'>('score');
  const [gapTier, setGapTier] = useState<1 | 2>(1);

  const toggleSection = (s: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s); else next.add(s);
      return next;
    });
  };

  const fetchProfile = useCallback(async (code: string) => {
    if (!code) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/v1/languages/${code.toLowerCase()}/profile`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setProfile(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch');
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const scanGaps = useCallback(async (tier: 1 | 2) => {
    const codes = tier === 1 ? TIER_1_CODES : [...CLAUDE_TIER_2];
    setGapLoading(true);
    setGapProgress(0);
    setGapData(null);
    const rows: GapRow[] = [];
    const batchSize = 10;
    for (let i = 0; i < codes.length; i += batchSize) {
      const batch = codes.slice(i, i + batchSize);
      const results = await Promise.allSettled(
        batch.map(code =>
          fetch(`${API_BASE}/api/v1/languages/${code}/profile`).then(r => r.ok ? r.json() : null)
        )
      );
      for (let j = 0; j < batch.length; j++) {
        const r = results[j];
        const code = batch[j] as string;
        if (r && r.status === 'fulfilled' && r.value) {
          const p = r.value as LangProfile;
          const gb = p.grambank_count ?? 0;
          const wa = p.wals_count ?? 0;
          const ph = p.phoneme_inventory?.total ?? 0;
          const lx = p.lexicon_entries ?? 0;
          const score = (gb / 195 * 100) + (wa / 160 * 100) + Math.min(ph, 100) + Math.min(lx / 20, 100);
          rows.push({ code, name: p.name || code, claudeTier: tier, grambank: gb, wals: wa, phonemes: ph, lexicon: lx, score: Math.round(score) });
        } else {
          rows.push({ code, name: code, claudeTier: tier, grambank: -1, wals: -1, phonemes: -1, lexicon: -1, score: 0 });
        }
      }
      setGapProgress(Math.min(i + batchSize, codes.length));
    }
    setGapData(rows);
    setGapLoading(false);
  }, []);

  const sortedGaps = gapData ? [...gapData].sort((a, b) => {
    if (gapSort === 'score') return a.score - b.score;
    return (a[gapSort] ?? 0) - (b[gapSort] ?? 0);
  }) : null;

  const claude = langCode ? getClaudeTier(langCode.toLowerCase()) : null;
  const glossika = claude ? getGlossikaTier(claude.tier) : null;

  return (
    <div>
      {/* Batch gap scanner */}
      <div className="mb-6 p-4 rounded bg-zinc-800/40 border border-zinc-700/30">
        <div className="flex flex-wrap gap-3 items-center mb-3">
          <span className="text-sm text-zinc-300 font-medium">Data Gap Scanner</span>
          <button
            onClick={() => { setGapTier(1); scanGaps(1); }}
            disabled={gapLoading}
            className="px-3 py-1.5 rounded bg-teal-700 hover:bg-teal-600 text-white text-xs font-medium disabled:opacity-50"
          >
            Scan Tier 1 ({TIER_1_CODES.length})
          </button>
          <button
            onClick={() => { setGapTier(2); scanGaps(2); }}
            disabled={gapLoading}
            className="px-3 py-1.5 rounded bg-amber-700 hover:bg-amber-600 text-white text-xs font-medium disabled:opacity-50"
          >
            Scan Tier 2 ({[...CLAUDE_TIER_2].length})
          </button>
          {gapLoading && <span className="text-xs text-zinc-500">Fetching {gapProgress}/{gapTier === 1 ? TIER_1_CODES.length : [...CLAUDE_TIER_2].length}...</span>}
        </div>

        {sortedGaps && (
          <div className="overflow-x-auto">
            <div className="text-xs text-zinc-500 mb-2">
              {sortedGaps.filter(r => r.grambank === 0).length} missing Grambank · {sortedGaps.filter(r => r.wals === 0).length} missing WALS · {sortedGaps.filter(r => r.phonemes === 0).length} missing Phonemes · {sortedGaps.filter(r => r.lexicon === 0).length} missing Lexicon
            </div>
            <table className="w-full text-xs min-w-[600px]">
              <thead>
                <tr className="text-zinc-500 border-b border-zinc-700/40">
                  <th className="text-left py-1 pr-2 w-16">Code</th>
                  <th className="text-left py-1 pr-2">Name</th>
                  <th className="text-right py-1 pr-2 cursor-pointer hover:text-zinc-300" onClick={() => setGapSort('grambank')}>
                    GB{gapSort === 'grambank' ? ' ▲' : ''}
                  </th>
                  <th className="text-right py-1 pr-2 cursor-pointer hover:text-zinc-300" onClick={() => setGapSort('wals')}>
                    WALS{gapSort === 'wals' ? ' ▲' : ''}
                  </th>
                  <th className="text-right py-1 pr-2 cursor-pointer hover:text-zinc-300" onClick={() => setGapSort('phonemes')}>
                    Phon{gapSort === 'phonemes' ? ' ▲' : ''}
                  </th>
                  <th className="text-right py-1 pr-2 cursor-pointer hover:text-zinc-300" onClick={() => setGapSort('lexicon')}>
                    Lex{gapSort === 'lexicon' ? ' ▲' : ''}
                  </th>
                  <th className="text-right py-1 cursor-pointer hover:text-zinc-300" onClick={() => setGapSort('score')}>
                    Score{gapSort === 'score' ? ' ▲' : ''}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedGaps.map(r => (
                  <tr
                    key={r.code}
                    className="border-b border-zinc-800/40 hover:bg-zinc-800/30 cursor-pointer"
                    onClick={() => { setLangCode(r.code); fetchProfile(r.code); }}
                  >
                    <td className="py-1 pr-2 font-mono text-zinc-300">{r.code}</td>
                    <td className="py-1 pr-2 text-zinc-400">{r.name}</td>
                    <td className={`py-1 pr-2 text-right font-mono ${r.grambank === 0 ? 'text-red-400' : r.grambank < 100 ? 'text-amber-400' : 'text-zinc-400'}`}>{r.grambank}</td>
                    <td className={`py-1 pr-2 text-right font-mono ${r.wals === 0 ? 'text-red-400' : r.wals < 50 ? 'text-amber-400' : 'text-zinc-400'}`}>{r.wals}</td>
                    <td className={`py-1 pr-2 text-right font-mono ${r.phonemes === 0 ? 'text-red-400' : 'text-zinc-400'}`}>{r.phonemes}</td>
                    <td className={`py-1 pr-2 text-right font-mono ${r.lexicon === 0 ? 'text-red-400' : 'text-zinc-400'}`}>{r.lexicon.toLocaleString()}</td>
                    <td className={`py-1 text-right font-mono ${r.score < 150 ? 'text-red-400' : r.score < 300 ? 'text-amber-400' : 'text-zinc-400'}`}>{r.score}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Single language lookup */}
      <div className="flex flex-wrap gap-3 items-center mb-6">
        <input
          type="text"
          value={langCode}
          onChange={e => setLangCode(e.target.value.toLowerCase())}
          onKeyDown={e => { if (e.key === 'Enter') fetchProfile(langCode); }}
          placeholder="Language code (e.g., pol, cym, slk)"
          className="w-full sm:w-64 px-3 py-2 rounded bg-zinc-800 border border-zinc-600 text-zinc-200 text-sm font-mono placeholder:text-zinc-600 focus:outline-none focus:border-teal-500"
        />
        <button
          onClick={() => fetchProfile(langCode)}
          disabled={!langCode || loading}
          className="px-4 py-2 rounded bg-teal-700 hover:bg-teal-600 text-white text-sm font-medium disabled:opacity-50"
        >
          {loading ? 'Loading...' : 'Lookup'}
        </button>
      </div>

      {error && <div className="text-red-400 text-sm mb-4">{error}</div>}

      {langCode && claude && (
        <div className="mb-6 p-4 rounded bg-zinc-800/60 border border-zinc-700/40">
          <div className="flex flex-wrap gap-4 items-baseline">
            <span className="font-mono text-lg text-zinc-200">{langCode.toUpperCase()}</span>
            {profile?.name && profile.name !== langCode && (
              <span className="text-zinc-300">{profile.name}</span>
            )}
            <span className={`text-sm font-medium ${claude.color}`}>{claude.label}</span>
            <span className="text-sm text-zinc-400">→ {glossika?.label}</span>
          </div>
          <div className="mt-2 text-xs text-zinc-500">{glossika?.ops}</div>
          {profile?.family && (
            <div className="mt-1 text-xs text-zinc-500">Family: {profile.family}</div>
          )}
          {profile?.classification && (
            <div className="mt-1 text-xs text-zinc-500 truncate">{profile.classification}</div>
          )}
          {profile?.macroarea && (
            <span className="mt-1 text-xs text-zinc-600 ml-2">{profile.macroarea}</span>
          )}
        </div>
      )}

      {profile && (
        <div className="space-y-4">
          {/* Data availability summary */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {[
              { label: 'Grambank', count: profile.grambank_count ?? 0, max: 195 },
              { label: 'WALS', count: profile.wals_count ?? 0, max: 160 },
              { label: 'Phonemes', count: profile.phoneme_inventory?.total ?? 0, max: null },
              { label: 'Lexicon', count: profile.lexicon_entries ?? 0, max: null },
              { label: 'Coordinates', count: profile.coordinates ? 1 : 0, max: 1 },
            ].map(d => (
              <div key={d.label} className="p-3 rounded bg-zinc-800/40 border border-zinc-700/30 text-center">
                <div className="text-lg font-mono text-zinc-200">{d.count.toLocaleString()}</div>
                <div className="text-xs text-zinc-500">{d.label}{d.max && d.max > 1 ? ` / ${d.max}` : ''}</div>
                {d.max && d.count === 0 && <div className="text-xs text-red-400 mt-1">missing</div>}
                {d.max && d.max > 1 && d.count > 0 && d.count < d.max * 0.5 && <div className="text-xs text-amber-400 mt-1">partial</div>}
              </div>
            ))}
          </div>

          {/* Phoneme inventory */}
          {(profile.phoneme_inventory?.total ?? 0) > 0 && (
            <div className="border border-zinc-700/40 rounded">
              <button
                onClick={() => toggleSection('phonemes')}
                className="w-full px-4 py-3 flex justify-between items-center text-left hover:bg-zinc-800/40"
              >
                <span className="text-sm font-medium text-zinc-200">
                  Phoneme Inventory ({profile.phoneme_inventory!.consonants} C, {profile.phoneme_inventory!.vowels} V{profile.phoneme_inventory!.tones > 0 ? `, ${profile.phoneme_inventory!.tones} T` : ''})
                </span>
                <span className="text-zinc-500">{expandedSections.has('phonemes') ? '▼' : '▶'}</span>
              </button>
              {expandedSections.has('phonemes') && (
                <div className="px-4 pb-3">
                  {profile.phoneme_inventory!.consonant_list.length > 0 && (
                    <div className="mb-2">
                      <div className="text-xs text-zinc-500 mb-1">Consonants ({profile.phoneme_inventory!.consonant_list.length})</div>
                      <div className="flex flex-wrap gap-1">
                        {profile.phoneme_inventory!.consonant_list.map(p => (
                          <span key={p} className="px-1.5 py-0.5 rounded text-xs font-mono bg-blue-900/30 text-blue-300">{p}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {profile.phoneme_inventory!.vowel_list.length > 0 && (
                    <div className="mb-2">
                      <div className="text-xs text-zinc-500 mb-1">Vowels ({profile.phoneme_inventory!.vowel_list.length})</div>
                      <div className="flex flex-wrap gap-1">
                        {profile.phoneme_inventory!.vowel_list.map(p => (
                          <span key={p} className="px-1.5 py-0.5 rounded text-xs font-mono bg-rose-900/30 text-rose-300">{p}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {profile.phoneme_inventory!.tone_list.length > 0 && (
                    <div className="mb-2">
                      <div className="text-xs text-zinc-500 mb-1">Tones ({profile.phoneme_inventory!.tone_list.length})</div>
                      <div className="flex flex-wrap gap-1">
                        {profile.phoneme_inventory!.tone_list.map(p => (
                          <span key={p} className="px-1.5 py-0.5 rounded text-xs font-mono bg-amber-900/30 text-amber-300">{p}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Typological features (Grambank + WALS combined) */}
          {profile.typology && Object.keys(profile.typology).length > 0 && (() => {
            const gbEntries = Object.entries(profile.typology!).filter(([k]) => k.startsWith('gb_'));
            const walsEntries = Object.entries(profile.typology!).filter(([k]) => k.startsWith('wals_'));
            return (
              <>
                {gbEntries.length > 0 && (
                  <div className="border border-zinc-700/40 rounded">
                    <button
                      onClick={() => toggleSection('grambank')}
                      className="w-full px-4 py-3 flex justify-between items-center text-left hover:bg-zinc-800/40"
                    >
                      <span className="text-sm font-medium text-zinc-200">Grambank Features ({gbEntries.length})</span>
                      <span className="text-zinc-500">{expandedSections.has('grambank') ? '▼' : '▶'}</span>
                    </button>
                    {expandedSections.has('grambank') && (
                      <div className="px-4 pb-3 max-h-96 overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-zinc-500 border-b border-zinc-700/40">
                              <th className="text-left py-1 pr-2 w-20">ID</th>
                              <th className="text-left py-1 pr-2">Feature</th>
                              <th className="text-left py-1 w-24">Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {gbEntries.sort(([a],[b]) => a.localeCompare(b)).map(([key, feat]) => (
                              <tr key={key} className="border-b border-zinc-800/40">
                                <td className="py-1 pr-2 font-mono text-zinc-400">{key.replace('gb_','')}</td>
                                <td className="py-1 pr-2 text-zinc-300">{feat.name}</td>
                                <td className="py-1">
                                  <span className={`px-1.5 py-0.5 rounded text-xs ${
                                    feat.value === '1' ? 'bg-teal-900/40 text-teal-300' :
                                    feat.value === '0' ? 'bg-zinc-700/40 text-zinc-400' :
                                    'bg-amber-900/40 text-amber-300'
                                  }`}>
                                    {feat.label}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {walsEntries.length > 0 && (
                  <div className="border border-zinc-700/40 rounded">
                    <button
                      onClick={() => toggleSection('wals')}
                      className="w-full px-4 py-3 flex justify-between items-center text-left hover:bg-zinc-800/40"
                    >
                      <span className="text-sm font-medium text-zinc-200">WALS Features ({walsEntries.length})</span>
                      <span className="text-zinc-500">{expandedSections.has('wals') ? '▼' : '▶'}</span>
                    </button>
                    {expandedSections.has('wals') && (
                      <div className="px-4 pb-3 max-h-96 overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-zinc-500 border-b border-zinc-700/40">
                              <th className="text-left py-1 pr-2 w-20">ID</th>
                              <th className="text-left py-1 pr-2">Feature</th>
                              <th className="text-left py-1 w-40">Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {walsEntries.sort(([a],[b]) => a.localeCompare(b)).map(([key, feat]) => (
                              <tr key={key} className="border-b border-zinc-800/40">
                                <td className="py-1 pr-2 font-mono text-zinc-400">{key.replace('wals_','')}</td>
                                <td className="py-1 pr-2 text-zinc-300">{feat.name}</td>
                                <td className="py-1 text-zinc-300 text-xs">{feat.label}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </>
            );
          })()}

          {/* No data message */}
          {(profile.grambank_count ?? 0) === 0 && (profile.wals_count ?? 0) === 0 && (profile.phoneme_inventory?.total ?? 0) === 0 && (
            <div className="p-4 rounded bg-zinc-800/40 border border-zinc-700/30 text-center text-zinc-500 text-sm">
              No linguistics data available for this language.
              {claude && claude.tier <= 2 && (
                <div className="mt-2 text-amber-400">This is a high-priority language — data import needed.</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// --- Main Linguistics Page ---

async function saveConfigToApi(cfg: TranscriptionPatternsConfig): Promise<void> {
  const res = await fetch(`${API_BASE}/api/admin/transcription-patterns`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(cfg),
  });
  if (!res.ok) throw new Error(`Save failed: HTTP ${res.status}`);
}

interface DataSourceRow {
  source: string;
  table_name: string;
  last_dump_date: string;
  last_run: string;
  items_processed: number;
  status: string;
}

const STATUS_COLORS: Record<string, string> = {
  imported: 'text-teal-400',
  normalized: 'text-teal-400',
  merging: 'text-amber-400',
  normalizing: 'text-amber-400',
  scraping: 'text-amber-400',
  no_changes: 'text-zinc-400',
  extract_failed: 'text-red-400',
  import_failed: 'text-red-400',
  normalize_failed: 'text-red-400',
};

function DataSourcesTab() {
  const [sources, setSources] = useState<DataSourceRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    fetch(`${API_BASE}/api/admin/data-sources`)
      .then(r => r.ok ? r.json() : Promise.reject(`HTTP ${r.status}`))
      .then(data => { setSources(data.sources || []); setError(null); })
      .catch(e => setError(String(e)))
      .finally(() => setLoading(false));
  }, []);

  const grouped = sources.reduce<Record<string, DataSourceRow[]>>((acc, s) => {
    (acc[s.source] = acc[s.source] || []).push(s);
    return acc;
  }, {});

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-zinc-400">{sources.length} records across {Object.keys(grouped).length} sources</span>
        <button
          onClick={() => {
            setLoading(true);
            fetch(`${API_BASE}/api/admin/data-sources`)
              .then(r => r.json())
              .then(data => setSources(data.sources || []))
              .finally(() => setLoading(false));
          }}
          className="px-3 py-1.5 rounded bg-zinc-700 hover:bg-zinc-600 text-white text-xs font-medium"
        >
          Refresh
        </button>
      </div>

      {loading && <div className="text-sm text-zinc-400">Loading...</div>}
      {error && <div className="text-sm text-red-400">Error: {error}</div>}

      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]">
          <thead>
            <tr className="border-b border-zinc-700 text-zinc-400 text-xs">
              <th className="text-left py-2 px-3">Source</th>
              <th className="text-left py-2 px-3">Target Table</th>
              <th className="text-right py-2 px-3">Items</th>
              <th className="text-left py-2 px-3">Status</th>
              <th className="text-left py-2 px-3">Dump Date</th>
              <th className="text-left py-2 px-3">Last Run</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([source, rows]) =>
              rows.map((r, i) => (
                <tr key={`${r.source}-${r.table_name}`} className="border-b border-zinc-800 hover:bg-zinc-800/40">
                  <td className="py-2 px-3 font-mono text-zinc-200">{i === 0 ? r.source : ''}</td>
                  <td className="py-2 px-3 font-mono text-zinc-400">{r.table_name}</td>
                  <td className="py-2 px-3 text-right font-mono text-zinc-300">{(r.items_processed || 0).toLocaleString()}</td>
                  <td className={`py-2 px-3 font-medium ${STATUS_COLORS[r.status] || 'text-zinc-300'}`}>{r.status}</td>
                  <td className="py-2 px-3 text-zinc-400">{r.last_dump_date || '-'}</td>
                  <td className="py-2 px-3 text-zinc-500 text-xs">{r.last_run ? new Date(r.last_run).toLocaleDateString() : '-'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Linguistics() {
  const [tab, setTab] = useState<Tab>('transcription');
  const [config, setConfig] = useState<TranscriptionPatternsConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [search, setSearch] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const apiRes = await fetch(`${API_BASE}/api/admin/transcription-patterns`);
        if (apiRes.ok) {
          const data = await apiRes.json() as TranscriptionPatternsConfig;
          setConfig(data);
          setTranscriptionPatterns(data);
          setLoading(false);
          return;
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load config from API');
        setLoading(false);
      }
    })();
  }, []);

  const allAssigned = new Set<string>();
  if (config) {
    for (const p of Object.values(config.patterns)) {
      for (const lang of p.languages) allAssigned.add(lang);
    }
  }

  const updateConfig = useCallback((next: TranscriptionPatternsConfig) => {
    setConfig(next);
    setTranscriptionPatterns(next);
    setSaveStatus('saving');
    saveConfigToApi(next)
      .then(() => {
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus(s => s === 'saved' ? 'idle' : s), 2000);
      })
      .catch(() => setSaveStatus('error'));
  }, []);

  const handleRemoveLang = useCallback((patternKey: string, lang: string) => {
    if (!config) return;
    const pattern = config.patterns[patternKey];
    if (!pattern) return;
    const next: TranscriptionPatternsConfig = {
      ...config,
      patterns: {
        ...config.patterns,
        [patternKey]: {
          ...pattern,
          languages: pattern.languages.filter(l => l !== lang),
        },
      },
    };
    updateConfig(next);
  }, [config, updateConfig]);

  const handleAddLang = useCallback((patternKey: string, lang: string) => {
    if (!config) return;
    const pattern = config.patterns[patternKey];
    if (!pattern) return;
    const next: TranscriptionPatternsConfig = {
      ...config,
      patterns: {
        ...config.patterns,
        [patternKey]: {
          ...pattern,
          languages: [...pattern.languages, lang],
        },
      },
    };
    updateConfig(next);
  }, [config, updateConfig]);

  const handleMoveLang = useCallback((lang: string, fromKey: string, toKey: string) => {
    if (!config) return;
    const from = config.patterns[fromKey];
    const to = config.patterns[toKey];
    if (!from || !to) return;
    const next: TranscriptionPatternsConfig = {
      ...config,
      patterns: {
        ...config.patterns,
        [fromKey]: { ...from, languages: from.languages.filter(l => l !== lang) },
        [toKey]: { ...to, languages: [...to.languages, lang] },
      },
    };
    updateConfig(next);
  }, [config, updateConfig]);

  const tabClass = (t: Tab) =>
    `px-4 py-2 text-sm font-medium rounded-t transition-colors ${
      tab === t
        ? 'bg-zinc-800 text-teal-300 border-b-2 border-teal-500'
        : 'text-zinc-500 hover:text-zinc-300'
    }`;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <span className="text-zinc-400">Loading linguistics data...</span>
      </div>
    );
  }

  if (error || !config) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
        <h1 className="font-heading text-2xl font-bold text-red-400 mb-4">Error</h1>
        <p className="font-body text-lg text-zinc-300">{error ?? 'No config loaded'}</p>
      </div>
    );
  }

  const patternKeys = Object.keys(config.patterns).sort();
  const totalLangs = allAssigned.size;

  const searchUpper = search.trim().toUpperCase();
  let searchResult: { lang: string; pattern: string } | null = null;
  if (searchUpper) {
    for (const [key, p] of Object.entries(config.patterns)) {
      if (p.languages.indexOf(searchUpper) !== -1) {
        searchResult = { lang: searchUpper, pattern: key };
        break;
      }
    }
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-gray-100">Linguistics</h1>
          <p className="text-sm text-zinc-400 mt-1">
            Transcription patterns, grammar patterns, and coverage analysis
          </p>
        </div>
        {tab === 'transcription' && (
          <div className="flex items-center gap-3">
            {saveStatus === 'saving' && <span className="text-xs text-amber-400 font-medium animate-pulse">Saving...</span>}
            {saveStatus === 'saved' && <span className="text-xs text-teal-400 font-medium">Saved</span>}
            {saveStatus === 'error' && <span className="text-xs text-red-400 font-medium">Save failed</span>}
          </div>
        )}
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-zinc-700 mb-6">
        <button onClick={() => setTab('transcription')} className={tabClass('transcription')}>
          Transcription Patterns
        </button>
        <button onClick={() => setTab('grammar')} className={tabClass('grammar')}>
          Grammar Patterns
        </button>
        <button onClick={() => setTab('coverage')} className={tabClass('coverage')}>
          Coverage
        </button>
        <button onClick={() => setTab('langdata')} className={tabClass('langdata')}>
          Language Data
        </button>
        <button onClick={() => setTab('datasources')} className={tabClass('datasources')}>
          Data Sources
        </button>
      </div>

      {/* Tab content */}
      {tab === 'transcription' && (
        <>
          <div className="mb-2 text-sm text-zinc-400">
            {totalLangs} languages across {patternKeys.length} patterns
          </div>

          {/* Search / lookup */}
          <div className="mb-6 flex items-center gap-3">
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Look up language code (e.g. THA)"
              className="w-full sm:w-64 px-3 py-2 rounded bg-zinc-800 border border-zinc-600 text-zinc-200 text-sm font-mono placeholder:text-zinc-600 focus:outline-none focus:border-teal-500"
            />
            {searchUpper && (
              searchResult ? (
                <span className="text-sm text-zinc-300">
                  <span className="font-mono text-teal-300">{searchResult.lang}</span>
                  {' '}is in pattern{' '}
                  <PatternBadge code={searchResult.pattern} />
                  {' '}
                  <span className="text-zinc-500">({config.patterns[searchResult.pattern]?.title})</span>
                  {patternKeys.length > 1 && (
                    <span className="ml-3 text-xs text-zinc-500">
                      Move to:{' '}
                      {patternKeys.filter(k => k !== searchResult!.pattern).map(k => (
                        <button
                          key={k}
                          onClick={() => handleMoveLang(searchResult!.lang, searchResult!.pattern, k)}
                          className="ml-1 px-1.5 py-0.5 rounded bg-zinc-700 text-zinc-300 hover:bg-zinc-600 transition-colors"
                        >
                          {k}
                        </button>
                      ))}
                    </span>
                  )}
                </span>
              ) : (
                <span className="text-sm text-amber-400">
                  <span className="font-mono">{searchUpper}</span> not assigned to any pattern
                </span>
              )
            )}
          </div>

          {/* Pattern cards */}
          <div className="space-y-4">
            {patternKeys.map(key => (
              <PatternCard
                key={key}
                code={key}
                pattern={config.patterns[key]!}
                onRemoveLang={lang => handleRemoveLang(key, lang)}
                onAddLang={lang => handleAddLang(key, lang)}
                allAssigned={allAssigned}
              />
            ))}
          </div>

          {/* Translation languages reference */}
          <div className="mt-8 rounded-lg border border-white/5 bg-zinc-900/30 p-4">
            <h2 className="font-heading text-lg font-semibold text-zinc-200 mb-3">Translation Languages</h2>
            <p className="text-xs text-zinc-500 mb-3">
              Base-language code to standard written form. Translations in sample sentences use these forms.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
              {Object.entries(config.translation_languages).map(([code, name]) => (
                <div key={code} className="flex items-center gap-2 px-2 py-1 rounded bg-zinc-800">
                  <span className="font-mono text-xs text-teal-300">{code}</span>
                  <span className="text-xs text-zinc-300">{name}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {tab === 'grammar' && <GrammarTab />}
      {tab === 'coverage' && <CoverageTab />}
      {tab === 'langdata' && <LangDataTab />}
      {tab === 'datasources' && <DataSourcesTab />}
    </div>
  );
}
